import { Button } from 'base/ui/button';
import { Dialog } from 'base/ui/dialog';
import { Rows } from 'base/ui/layout';
import { TextRegular, TextRegularLight } from 'base/ui/typography';
import { makeObservable, observable } from 'mobx';
import React from 'react';

export class EditingActionConfirmations {
  @observable.ref private current:
    | {
        dialog: React.ReactNode;
        hide: () => void;
      }
    | undefined = undefined;

  constructor() {
    makeObservable(this);
  }

  get currentDialog() {
    return this.current?.dialog;
  }

  confirmDeleteLastGlobal(section: { name: string }): Promise<boolean> {
    return this.runDialog(({ onOk, onCancel }) => (
      <Dialog
        title="Delete global section?"
        onClose={onCancel}
        buttons={
          <>
            <Button size="large" label="Cancel" onClick={onCancel} />
            <Button
              size="large"
              variant="primary"
              background="negative"
              label="Delete"
              onClick={onOk}
            />
          </>
        }
      >
        <Rows spacing={16}>
          <TextRegularLight>
            You are about to delete the final instance of the global section{' '}
            <TextRegular tag="span">{section.name}</TextRegular>. If you delete
            it, the global section will be removed from your project.
          </TextRegularLight>
        </Rows>
      </Dialog>
    ));
  }

  confirmDeleteSpace(name: string): Promise<boolean> {
    return this.runDialog(({ onOk, onCancel }) => (
      <Dialog
        title="Delete this sitemap?"
        onClose={onCancel}
        buttons={
          <>
            <Button label="Cancel" onClick={onCancel} size="large" />
            <Button
              size="large"
              variant="primary"
              background="negative"
              label="Delete"
              onClick={onOk}
            />
          </>
        }
      >
        <Rows spacing={16}>
          <TextRegularLight>
            You are about to delete <TextRegular tag="span">{name}</TextRegular>
            .
          </TextRegularLight>
        </Rows>
      </Dialog>
    ));
  }

  confirmReplaceMatchingSections(section: { name: string }): Promise<boolean> {
    return this.runDialog(({ onOk, onCancel }) => (
      <Dialog
        title="Replace all?"
        onClose={onCancel}
        buttons={
          <>
            <Button size="large" label="Don’t replace" onClick={onCancel} />
            <Button
              size="large"
              variant="primary"
              label="Replace"
              onClick={onOk}
            />
          </>
        }
      >
        <TextRegularLight>
          Do you want to replace all{' '}
          <TextRegular tag="span">{section.name}</TextRegular> sections in your
          project with this global section?
        </TextRegularLight>
      </Dialog>
    ));
  }

  private runDialog(
    Comp: React.ComponentType<{
      onOk: () => void;
      onCancel: () => void;
    }>,
  ): Promise<boolean> {
    this.current?.hide();
    return new Promise((resolve) => {
      const onOk = () => {
        resolve(true);
        if (this.current === val) this.current = undefined;
      };
      const onCancel = () => {
        resolve(false);
        if (this.current === val) this.current = undefined;
      };
      const val = {
        dialog: <Comp onOk={onOk} onCancel={onCancel} />,
        hide: onCancel,
      };
      this.current = val;
    });
  }
}
